class Draggable {
    constructor() {
        this.draggables = [];
        this.activeElement = null;
        this.initialX = 0;
        this.initialY = 0;
        this.initialLeft = 0;
        this.initialTop = 0;
        this.draggableParent = null;
        this.highestZIndex = 1;
        this.snapBackDelay = 400; // Delay in milliseconds before snapping back
        this.initialZIndex = 1; // Store the initial z-index
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.moveThreshold = 100; // pixels to move before initiating drag
        this.isDragging = false;
        this.eventsBound = false;
        this.boundDragMouseDown = this.dragMouseDown.bind(this);
        this.boundElementDrag = this.elementDrag.bind(this);
        this.boundCloseDragElement = this.closeDragElement.bind(this);
        this.boundDragTouchStart = this.dragTouchStart.bind(this);
        this.boundElementTouchDrag = this.elementTouchDrag.bind(this);
    }

    init() {
        this.draggableParent = document.querySelector('[data-draggable-parent]');
        if (this.draggableParent) {
            this.draggableParent.style.position = 'absolute';
            this.draggableParent.style.top = '0';
            this.draggableParent.style.left = '0';
            this.draggableParent.style.bottom = '0';
            this.draggableParent.style.right = '0';
        }

        this.draggables = document.querySelectorAll('[data-draggable]');
        if (!this.draggables.length) {
            this.unbindEvents();
            return;
        }

        this.bindEvents();
        this.draggables.forEach(el => {
            el.style.position = 'relative';
            el.style.userSelect = 'none';
            el.style.zIndex = this.initialZIndex; // Set initial z-index

            const titleBar = el.querySelector('.title-bar');
            if (titleBar) {
                titleBar.style.cursor = 'move';
            } else {
                el.style.cursor = 'move';
            }
        });
    }

    bindEvents() {
        if (this.eventsBound) return;

        document.addEventListener('mousedown', this.boundDragMouseDown);
        document.addEventListener('mousemove', this.boundElementDrag);
        document.addEventListener('mouseup', this.boundCloseDragElement);
        document.addEventListener('touchstart', this.boundDragTouchStart, { passive: false });
        document.addEventListener('touchmove', this.boundElementTouchDrag, { passive: false });
        document.addEventListener('touchend', this.boundCloseDragElement);

        this.eventsBound = true;
    }

    unbindEvents() {
        if (!this.eventsBound) return;

        document.removeEventListener('mousedown', this.boundDragMouseDown);
        document.removeEventListener('mousemove', this.boundElementDrag);
        document.removeEventListener('mouseup', this.boundCloseDragElement);
        document.removeEventListener('touchstart', this.boundDragTouchStart, { passive: false });
        document.removeEventListener('touchmove', this.boundElementTouchDrag, { passive: false });
        document.removeEventListener('touchend', this.boundCloseDragElement);

        this.eventsBound = false;
    }

    bringToFront(element) {
        this.highestZIndex++;
        element.style.zIndex = this.highestZIndex;
    }

    dragMouseDown(e) {
        const draggableEl = e.target.closest('[data-draggable]');
        if (!draggableEl) return;

        const titleBar = e.target.closest('.title-bar');
        if (!titleBar || !draggableEl.contains(titleBar)) return;

        if (e.target.closest('.title-bar-controls')) return;

        e.preventDefault();
        this.activeElement = draggableEl;

        this.bringToFront(this.activeElement);

        this.initialLeft = parseInt(this.activeElement.style.left) || 0;
        this.initialTop = parseInt(this.activeElement.style.top) || 0;

        if (e.type === 'touchstart') {
            this.initialX = e.touches[0].clientX - this.initialLeft;
            this.initialY = e.touches[0].clientY - this.initialTop;
        } else {
            this.initialX = e.clientX - this.initialLeft;
            this.initialY = e.clientY - this.initialTop;
        }
    }

    elementDrag(e) {
        if (this.activeElement) {
            e.preventDefault();
            
            let currentX, currentY;
            if (e.type === 'touchmove') {
                currentX = e.touches[0].clientX;
                currentY = e.touches[0].clientY;
            } else {
                currentX = e.clientX;
                currentY = e.clientY;
            }

            const newPosX = currentX - this.initialX;
            const newPosY = currentY - this.initialY;

            this.activeElement.style.left = newPosX + 'px';
            this.activeElement.style.top = newPosY + 'px';
        }
    }

    closeDragElement() {
        if (this.activeElement) {
            const element = this.activeElement; // Store reference to active element
            if (element.querySelector('.window-top')) {
                const windowTop = element.querySelector('.window-top');
                windowTop.style.cursor = 'default';
            } else {
                element.style.cursor = 'default';
            }

            setTimeout(() => {
                this.snapBack(element);
            }, this.snapBackDelay);

            this.activeElement = null;
        }
    }

    snapBack(element) {
        const startLeft = parseInt(element.style.left) || 0;
        const startTop = parseInt(element.style.top) || 0;
        const startTime = performance.now();
        const duration = 300; // Animation duration in milliseconds

        const animate = (currentTime) => {
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < duration) {
                const progress = elapsedTime / duration;
                const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
                element.style.left = (startLeft * (1 - easeProgress)) + 'px';
                element.style.top = (startTop * (1 - easeProgress)) + 'px';
                requestAnimationFrame(animate);
            } else {
                element.style.left = '0px';
                element.style.top = '0px';
                element.style.zIndex = this.initialZIndex; // Reset z-index to initial value
            }
        };

        requestAnimationFrame(animate);
    }

    dragTouchStart(e) {
        const draggableEl = e.target.closest('[data-draggable]');
        if (!draggableEl) return;

        // Disable touch-drag entirely on mobile -- it conflicts with scrolling/swiping
        if (window.innerWidth < 900) return;

        const titleBar = e.target.closest('.title-bar');
        if (!titleBar || !draggableEl.contains(titleBar)) return;
        if (e.target.closest('.title-bar-controls')) return;

        e.preventDefault();
        this.activeElement = draggableEl;
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
        this.initialLeft = parseInt(this.activeElement.style.left) || 0;
        this.initialTop = parseInt(this.activeElement.style.top) || 0;
        this.isDragging = false;
    }

    elementTouchDrag(e) {
        if (this.activeElement) {
            const touchX = e.touches[0].clientX;
            const touchY = e.touches[0].clientY;
            const deltaX = touchX - this.touchStartX;
            const deltaY = touchY - this.touchStartY;

            if (!this.isDragging) {
                if (Math.abs(deltaX) > this.moveThreshold || Math.abs(deltaY) > this.moveThreshold) {
                    this.isDragging = true;
                    this.bringToFront(this.activeElement);
                    if (this.activeElement.querySelector('.window-top')) {
                        const windowTop = this.activeElement.querySelector('.window-top');
                        windowTop.style.cursor = 'move';
                    } else {
                        this.activeElement.style.cursor = 'move';
                    }
                } else {
                    return;
                }
            }

            e.preventDefault();
            const newPosX = this.initialLeft + deltaX;
            const newPosY = this.initialTop + deltaY;

            this.activeElement.style.left = newPosX + 'px';
            this.activeElement.style.top = newPosY + 'px';
        }
    }
}

// Initialize the Draggable instance
const draggable = new Draggable();
document.addEventListener('DOMContentLoaded', () => draggable.init());
